import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/query-keys";

/** `Set` does not round-trip through JSON; cache stores `string[]` and we map to Set in `select`. */
export function coerceTrendingAssetIds(data: unknown): Set<string> {
  if (data instanceof Set) return data;
  if (Array.isArray(data)) {
    return new Set(data.filter((x): x is string => typeof x === "string"));
  }
  return new Set();
}

/**
 * Uses the `get_trending_asset_ids` RPC which aggregates scores in the DB
 * and returns at most 100 IDs — avoids fetching unbounded raw rows from
 * the purchases/reviews tables just to collect unique asset IDs.
 */
async function fetchTrendingAssetIds(): Promise<string[]> {
  const { data, error } = await supabase.rpc("get_trending_asset_ids", {
    p_limit: 100,
    p_window_days: 7,
  });
  if (error) {
    // Trending badges are decorative; never block the page or alert on this.
    // PGRST202 is the migration-not-yet-applied case, but any transient
    // network/RPC failure should degrade the same way — return [].
    console.warn("[useTrendingAssets] failed, degrading to no badges:", error.message);
    return [];
  }
  return ((data ?? []) as { id: string }[]).map((r) => r.id);
}

export function useTrendingAssets() {
  return useQuery({
    queryKey: queryKeys.assets.trending(),
    queryFn: fetchTrendingAssetIds,
    select: (data: string[]) => coerceTrendingAssetIds(data),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
